/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import { Button } from "@chakra-ui/react";
import { Tooltip, IconButton } from "@mui/material";
import { MdInfoOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import AccountResetPostbackSecretConfirmModal from "../../shared/AccountResetPostbackSecretConfirmModal";
import AccountResetPostbackSecretSuccessModal from "../../shared/AccountResetPostbackSecretSuccessModal";
import { TokenManager } from "utils/tokenManager";

const GeneralTab: React.FC<{
  data: any;
  setSubaccountDetails: any;
  setSwalProps: any;
  isOtherTabUpdating: boolean;
  setIsUpdating: any;
  fetchSubaccountDetails: any;
  isLoading: boolean;
}> = ({
  data,
  setSubaccountDetails,
  setSwalProps,
  isOtherTabUpdating,
  setIsUpdating,
  fetchSubaccountDetails,
  isLoading,
}) => {
  const [isLocalUpdating, setIsLocalUpdating] = useState<boolean>(false);
  const [isInvalidURL, setIsInvalidURL] = useState<boolean>(false);
  const [postbackSecret, setPostbackSecret] = useState<string>("");
  const [
    isOpenResetPostbackSecretConfirmModal,
    setIsOpenResetPostbackSecretConfirmModal,
  ] = useState<boolean>(false);
  const [resetPostbackSecretInfo, setResetPostbackSecretInfo] =
    useState<any>(null);
  const [
    isOpenResetPostbackSecretSuccessModal,
    setIsOpenResetPostbackSecretSuccessModal,
  ] = useState<boolean>(false);

  useEffect(() => {
    if (data?.postback_secret) {
      setPostbackSecret(data.postback_secret);
    }
  }, [data]);

  const showResetPostbackSecretConfirmModal = () => {
    setIsOpenResetPostbackSecretConfirmModal(true);
  };

  const closeResetPostbackSecretConfirmModal = () => {
    setIsOpenResetPostbackSecretConfirmModal(false);
  };

  const showResetPostbackSecretSuccessModal = () => {
    setIsOpenResetPostbackSecretSuccessModal(true);
  };

  const closeResetPostbackSecretSuccessModal = () => {
    setResetPostbackSecretInfo(null);
    setIsOpenResetPostbackSecretSuccessModal(false);
  };

  const isURL = (str: string): boolean => {
    const pattern =
      /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?(\?.*)?(#.*)?$/i;
    return pattern.test(str);
  };

  const handleInputChange = async (event: any) => {
    setIsInvalidURL(false);
    const { id, value } = event.target;
    setSubaccountDetails({ ...data, [id]: value });
    if (id === "postback_url") {
      if (!isURL(value)) {
        setIsInvalidURL(true);
      }
    }
  };

  const isFormValid = data !== null ? data.postback_url : false;

  const handleSubmit = async () => {
    if (isFormValid) {
      const postData = {
        id: data.id,
        postback_url: data.postback_url,
        username: localStorage.getItem("username"),
        userRole: localStorage.getItem("userRole"),
      };

      try {
        setIsLocalUpdating(true);
        setIsUpdating(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${localStorage
                .getItem("userRole")
                ?.toLowerCase()}/subaccount/action/account-update-settings`,
            postData
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: `Settings has been updated`,
                showConfirmButton: true,
                didClose: () => {
                  setIsLocalUpdating(false);
                  setIsUpdating(false);
                  setSwalProps({});
                },
              });
            } else {
              setIsLocalUpdating(false);
              setIsUpdating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsLocalUpdating(false);
                  setIsUpdating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsLocalUpdating(false);
            setIsUpdating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsLocalUpdating(false);
                setIsUpdating(false);
              },
            });
          });
      } catch (error) {
        setIsLocalUpdating(false);
        setIsUpdating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsLocalUpdating(false);
            setIsUpdating(false);
          },
        });
      }
    }
  };

  return (
    <>
      {isOpenResetPostbackSecretConfirmModal && data && (
        <AccountResetPostbackSecretConfirmModal
          details={data}
          isOpen={isOpenResetPostbackSecretConfirmModal}
          onClose={() => closeResetPostbackSecretConfirmModal()}
          setSwalProps={setSwalProps}
          showResetPostbackSecretSuccessModal={
            showResetPostbackSecretSuccessModal
          }
          setResetPostbackSecretInfo={setResetPostbackSecretInfo}
        />
      )}
      {isOpenResetPostbackSecretSuccessModal && resetPostbackSecretInfo && (
        <AccountResetPostbackSecretSuccessModal
          details={resetPostbackSecretInfo}
          isOpen={isOpenResetPostbackSecretSuccessModal}
          onClose={() => closeResetPostbackSecretSuccessModal()}
          triggerReload={fetchSubaccountDetails}
        />
      )}

      <div className="flex px-4 py-8">
        <div className="w-full">
          {data !== null ? (
            <>
              <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                  Postback URL
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-full md:w-1/2">
                    <InputField
                      hideLabel
                      id="postback_url"
                      label="Postback URL"
                      placeholder="https://example.com"
                      extra=""
                      disabled={isLocalUpdating || isOtherTabUpdating}
                      variant="auth"
                      value={data.postback_url}
                      onChange={handleInputChange}
                      error={
                        isInvalidURL
                          ? "Invalid URL format. Please enter a valid URL, e.g. https://example.com"
                          : undefined
                      }
                    />
                  </div>
                  <Button
                    onClick={() => handleSubmit()}
                    isDisabled={
                      !isFormValid ||
                      isLocalUpdating ||
                      isOtherTabUpdating ||
                      isInvalidURL ||
                      isLoading ||
                      TokenManager.isImpersonating()
                    }
                    isLoading={isLocalUpdating}
                    loadingText="Processing..."
                    className="btn btn-primary !py-2.5 !px-10"
                  >
                    Save
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                  Webhook Secret
                </div>
                <div className="mb-6">
                  <div className="flex w-full md:w-1/2">
                    <input
                      type="text"
                      value={postbackSecret}
                      onKeyDown={(e) => e.preventDefault()}
                      className="caret-transparent w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none"
                    />
                    <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                      <Tooltip
                        title="You will only see the unmask Webhook Secret once, immediately after it's generated."
                        arrow
                        placement="top"
                      >
                        <IconButton
                          id="button-info-postback-secret"
                          size="small"
                        >
                          <MdInfoOutline className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title="Reset Webhook Secret"
                        arrow
                        placement="top"
                      >
                        <IconButton
                          disabled={isLocalUpdating || isOtherTabUpdating || TokenManager.isImpersonating()}
                          id="button-reset-postback-secret"
                          size="small"
                          onClick={showResetPostbackSecretConfirmModal}
                        >
                          <TbRefresh className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="">
              <div
                className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              ></div>
              <div
                className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              ></div>
              <div
                className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              ></div>
              <div
                className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
                style={{ animationDuration: "0.6s" }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralTab;
